import { defineStore } from 'pinia'
import { getWalletStrategy } from '@shared/wallet'
import { alchemyRpcEndpoint } from '@shared/wallet'
import { injToken, usdtToken } from '@shared/data/token'
import { WalletStrategy } from '@injectivelabs/wallet-strategy'
import { getInjectiveAddress } from '@injectivelabs/sdk-ts/utils'
import { Wallet, isCosmosWallet } from '@injectivelabs/wallet-base'
import { CHAIN_ID, ETHEREUM_CHAIN_ID } from '@shared/utils/constant'
import { NEPTUNE_USDT_CW20_CONTRACT } from '@injectivelabs/sdk-ts/client/wasm'
import {
  web3Client,
  getStakingApi,
  getIndexerRestExplorerApi,
  getIndexerAccountPortfolioApi
} from '@shared/service'
import { DEFAULT_MIN_GAS } from '@/app/utils/constants'
import { getAccountDetails } from '@/app/services/account'
import { neptuneService, getIndexerGrpcArchiverApi } from '@/app/Services'
import { isPgtSubaccountId, isSgtSubaccountId } from '@/app/utils/helpers'
import {
  getDefaultAccountBalances,
  getNonDefaultSubaccountBalances
} from '@/app/client/utils/account'
import { BusEvents, HistoricalPortfolioDuration } from '@/types'
import type { Coin } from '@injectivelabs/ts-types'
import type { Delegation, AccountStats } from '@injectivelabs/sdk-ts'
import type { SubaccountBalance } from '@/types'

type AccountStoreState = {
  pubKey?: string
  subaccountId: string
  bankBalances: Coin[]
  singerInjBalance: string
  injDelegations: Delegation[]
  neptuneUsdtLendingApy: string
  neptuneUsdtRedemptionRatio: number
  erc20BalancesMap: Record<string, string>
  cw20Balances: { amount: string; address: string }[]
  accountStats: AccountStats | Record<string, number>
  subaccountBalancesMap: Record<string, SubaccountBalance[]>
}

const initialStateFactory = (): AccountStoreState => ({
  pubKey: '',
  bankBalances: [],
  cw20Balances: [],
  subaccountId: '',
  accountStats: {},
  injDelegations: [],
  singerInjBalance: '0',
  erc20BalancesMap: {},
  subaccountBalancesMap: {},
  neptuneUsdtLendingApy: '',
  neptuneUsdtRedemptionRatio: 0
})

export const useAccountStore = defineStore('account', {
  state: (): AccountStoreState => initialStateFactory(),
  getters: {
    hasSufficientGas: (state: AccountStoreState) => {
      const sharedWalletStore = useSharedWalletStore()

      return (
        sharedWalletStore.isWalletExemptFromGasFee ||
        toBigNumber(state.singerInjBalance).gt(DEFAULT_MIN_GAS)
      )
    },

    balancesMap: (state: AccountStoreState) => {
      if (state.bankBalances.length === 0) {
        return {}
      }

      return state.bankBalances.reduce(
        (list, balance) => {
          return { ...list, [balance.denom]: balance.amount }
        },
        {} as Record<string, string>
      )
    },

    cw20BalancesMap: (state: AccountStoreState) => {
      if (state.cw20Balances.length === 0) {
        return {}
      }

      return state.cw20Balances.reduce(
        (list, balance) => {
          return { ...list, [balance.address]: balance.amount }
        },
        {} as Record<string, string>
      )
    },

    defaultSubaccountBalances: (state: AccountStoreState) => {
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.authZOrDefaultSubaccountId) {
        return []
      }

      return state.subaccountBalancesMap[
        sharedWalletStore.authZOrDefaultSubaccountId
      ]
    },

    isDefaultSubaccount: (state: AccountStoreState) => {
      const sharedWalletStore = useSharedWalletStore()

      return sharedWalletStore.authZOrDefaultSubaccountId === state.subaccountId
    },

    hasMultipleSubaccounts: (state: AccountStoreState) => {
      return Object.keys(state.subaccountBalancesMap).length > 1
    },

    isSgtSubaccount: (state) =>
      !!isSgtSubaccountId(state.subaccountId) ||
      !!isPgtSubaccountId(state.subaccountId),

    hasBalance: (state) => {
      return (
        state.bankBalances.length > 0 ||
        Object.keys(state.subaccountBalancesMap).length > 1
      )
    },

    neptuneUsdtInBankBalance: (state) => {
      const sharedWalletStore = useSharedWalletStore()

      if (sharedWalletStore.isAuthzWalletConnected) {
        return 0
      }

      const neptuneUsdtBalance = state.cw20Balances.find(
        (balance) => balance.address === NEPTUNE_USDT_CW20_CONTRACT
      )?.amount

      if (!neptuneUsdtBalance) {
        return 0
      }

      return neptuneService.calculateBankAmount(
        Number(neptuneUsdtBalance),
        state.neptuneUsdtRedemptionRatio
      )
    }
  },
  actions: {
    cancelBankBalanceStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelBankBalanceStream'
    ),
    cancelSubaccountBalanceStream: lazyPiniaAction(
      () => import('./stream'),
      'cancelSubaccountBalanceStream'
    ),
    deposit: lazyPiniaAction(() => import('./message'), 'deposit'),
    transfer: lazyPiniaAction(() => import('./message'), 'transfer'),
    withdraw: lazyPiniaAction(() => import('./message'), 'withdraw'),
    withdrawToMain: lazyPiniaAction(
      () => import('./message'),
      'withdrawToMain'
    ),
    externalTransfer: lazyPiniaAction(
      () => import('./message'),
      'externalTransfer'
    ),
    streamBankBalance: lazyPiniaAction(
      () => import('./stream'),
      'streamBankBalance'
    ),
    streamSubaccountBalance: lazyPiniaAction(
      () => import('./stream'),
      'streamSubaccountBalance'
    ),
    convertNeptuneToPeggyUsdt: lazyPiniaAction(
      () => import('./neptune'),
      'convertNeptuneToPeggyUsdt'
    ),
    convertPeggyToNeptuneUsdt: lazyPiniaAction(
      () => import('./neptune'),
      'convertPeggyToNeptuneUsdt'
    ),
    fetchNeptuneRedemptionRatio: lazyPiniaAction(
      () => import('./neptune'),
      'fetchNeptuneRedemptionRatio'
    ),

    updateSubaccount(subaccountId: string) {
      const accountStore = useAccountStore()

      accountStore.subaccountId = subaccountId
      useEventBus(BusEvents.SubaccountChange).emit(subaccountId)
    },

    async fetchSignerInjBalance() {
      const indexerAccountPortfolioApi = await getIndexerAccountPortfolioApi()

      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const accountPortfolio =
        await indexerAccountPortfolioApi.fetchAccountPortfolioBalances(
          sharedWalletStore.injectiveAddress
        )

      const injBalance =
        accountPortfolio.bankBalancesList.find(
          ({ denom }) => denom === injToken.denom
        )?.amount || '0'

      accountStore.singerInjBalance = injBalance
    },

    async fetchAccountPortfolioBalances() {
      const indexerAccountPortfolioApi = await getIndexerAccountPortfolioApi()

      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const accountPortfolio =
        await indexerAccountPortfolioApi.fetchAccountPortfolioBalances(
          sharedWalletStore.authZOrInjectiveAddress
        )

      const defaultAccountBalances = getDefaultAccountBalances(
        accountPortfolio.subaccountsList,
        sharedWalletStore.authZOrDefaultSubaccountId
      )

      const nonDefaultSubaccounts = getNonDefaultSubaccountBalances(
        accountPortfolio.subaccountsList,
        sharedWalletStore.authZOrDefaultSubaccountId
      )

      // const subaccountId =
      //   accountStore.subaccountId || walletStore.authZOrDefaultSubaccountId

      accountStore.$patch((state) => {
        // state.subaccountId = subaccountId.includes(walletStore.authZOrAddress)
        //   ? subaccountId
        //   : walletStore.authZOrDefaultSubaccountId

        state.bankBalances = accountPortfolio.bankBalancesList || []

        state.subaccountBalancesMap = {
          [sharedWalletStore.authZOrDefaultSubaccountId]:
            defaultAccountBalances,
          ...nonDefaultSubaccounts
        }
      })
    },

    async fetchCw20Balances() {
      const indexerRestExplorerApi = await getIndexerRestExplorerApi()

      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const cw20Balances =
        await indexerRestExplorerApi.fetchCW20BalancesNoThrow(
          sharedWalletStore.authZOrInjectiveAddress
        )

      accountStore.cw20Balances = cw20Balances.map((balance) => ({
        address: balance.contract_address,
        amount: balance.balance
      }))

      const hasNeptuneUSDTCw20Balance = cw20Balances.some(
        ({ contract_address: address, balance }) =>
          address === NEPTUNE_USDT_CW20_CONTRACT && toBigNumber(balance).gt(0)
      )

      if (!hasNeptuneUSDTCw20Balance) {
        return
      }

      accountStore.fetchNeptuneRedemptionRatio()
    },

    async fetchErc20Balances() {
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()
      const spotStore = useSpotStore()

      const evmDenoms = spotStore.tradableDenoms.filter((denom) =>
        denom.startsWith('peggy')
      )

      const erc20Balances: Record<string, string> = {}

      await Promise.all(
        evmDenoms.map(async (denom) => {
          const { balance } = await web3Client.fetchTokenBalanceAndAllowance({
            address: sharedWalletStore.address,
            contractAddress: denom.replace('peggy', '')
          })

          if (toBigNumber(balance).gt(0)) {
            erc20Balances[denom] = balance
          }
        })
      )

      accountStore.erc20BalancesMap = erc20Balances
    },

    async fetchAddressFromWalletStrategy(wallet: Wallet) {
      try {
        const walletStrategy = new WalletStrategy({
          wallet,
          chainId: CHAIN_ID,
          evmOptions: {
            evmChainId: ETHEREUM_CHAIN_ID,
            rpcUrl: alchemyRpcEndpoint
          },
          strategies: {}
        })

        const addresses = await walletStrategy.enableAndGetAddresses()
        const [address] = addresses

        if (!address) {
          return
        }

        // cosmos returns inj address
        if (isCosmosWallet(wallet)) {
          return address
        }

        // eth returns eth address so convert to inj address
        return getInjectiveAddress(address)
      } catch {
        // silently fail
      }
    },

    async fetchPubKey(address: string) {
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()
      const walletStrategy = await getWalletStrategy()

      try {
        if (
          ([Wallet.Magic, Wallet.Turnkey] as Wallet[]).includes(
            sharedWalletStore.wallet
          )
        ) {
          const accountDetails = await getAccountDetails(address)
          const publicKeyBase64 = accountDetails.pubKey.key

          accountStore.pubKey = publicKeyBase64

          return
        }

        accountStore.pubKey = await walletStrategy.getPubKey(address)
      } catch {
        // silently fail
      }
    },

    async fetchNeptuneLendingApy() {
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const apr = await neptuneService.getLendingRateByDenom({
        denom: usdtToken.denom
      })

      if (!apr) {
        return
      }

      const neptuneUsdtLendingApy = neptuneService
        .calculateAPY(Number(apr))
        .toString()

      accountStore.neptuneUsdtLendingApy = neptuneUsdtLendingApy
    },

    async fetchAccountStats(
      period: HistoricalPortfolioDuration = HistoricalPortfolioDuration.OneMonth
    ) {
      const indexerGrpcArchiverApi = await getIndexerGrpcArchiverApi()

      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      const response = await indexerGrpcArchiverApi.fetchAccountStats({
        period,
        account: sharedWalletStore.authZOrInjectiveAddress
      })

      accountStore.accountStats = response
    },

    async fetchInjDelegations() {
      const stakingApi = await getStakingApi()

      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.authZOrInjectiveAddress) {
        return
      }

      const { delegations } = await stakingApi.fetchDelegationsNoThrow({
        injectiveAddress: sharedWalletStore.authZOrInjectiveAddress
      })

      const injDelegations = delegations.filter(
        (delegation) =>
          delegation.delegation.delegatorAddress ===
          sharedWalletStore.authZOrInjectiveAddress
      )

      accountStore.injDelegations = injDelegations
    },

    async reset() {
      const accountStore = useAccountStore()

      await Promise.all([
        accountStore.cancelBankBalanceStream(),
        accountStore.cancelSubaccountBalanceStream()
      ])

      accountStore.$reset()
    }
  }
})
